import { useMemo, useState, useCallback } from "react";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { ApiFactory } from "../../../core/config/ApiFactory";
import { AxiosError } from "axios";
import { clearState } from "./useChatPdVPersistence";
import { IResumoNaoLidas } from "../../interface/atendimento/IResumoNaoLidas";

export const useMensagens = () => {
    const [loadingChats, setLoadingChats] = useState(false);
    const [loadingSessoes, setLoadingSessoes] = useState(false);
    const [loadingAlerta, setLoadingAlerta] = useState(false);
    const [loadingEnvio, setLoadingEnvio] = useState(false);
    const showSnackbar = useSnackbar();
    const apiMarketplaces = useMemo(() => ApiFactory.getApi("MS_MARKETPLACES"), []);

    const listarChats = async (pagina = 1, limite = 30, busca?: string) => {
        if (loadingChats) {
            return null;
        }

        setLoadingChats(true);

        try {
            const params = new URLSearchParams({ pagina: String(pagina), limite: String(limite) });
            if (busca) params.set('busca', busca);
            const { data } = await apiMarketplaces.get(`/mensagens/chat?${params.toString()}`);

            if (!data) {
                showSnackbar("Ocorreu um erro ao listar as conversas", { severity: "error" });
                return null;
            }

            return data;
        } catch (error) {
            if (error instanceof AxiosError) {
                showSnackbar(error.response?.data.message, { severity: 'error' })
                return null;
            }

            showSnackbar("Ocorreu um erro ao listar as conversas", { severity: "error" });
            return null;
        } finally {
            setLoadingChats(false);
        }
    }

    const buscarChatPorUuid = useCallback(async (chatUuid: string) => {
        try {
            const { data } = await apiMarketplaces.get(`/mensagens/chat/${chatUuid}`);
            return data ?? null;
        } catch {
            return null;
        }
    }, [apiMarketplaces]);

    const listarSessoes = async (chatUuid: string, pagina: number, limite: number) => {
        if (loadingSessoes) {
            return [];
        }

        setLoadingSessoes(true);

        try {
            const { data } = await apiMarketplaces.get(`/mensagens/chat/${chatUuid}/sessoes?pagina=${pagina}&limite=${limite}`);

            if (!data) {
                showSnackbar("Ocorreu um erro ao listar as sessões", { severity: "error" });
                return [];
            }

            return data;
        } catch (error) {
            if (error instanceof AxiosError) {
                showSnackbar(error.response?.data.message, { severity: 'error' })
                return [];
            }

            showSnackbar("Ocorreu um erro ao listar as sessões", { severity: "error" });
            return [];
        } finally {
            setLoadingSessoes(false);
        }
    }

    const removerAlerta = async (chatUuid: string) => {
        if (loadingSessoes) {
            return null;
        }

        setLoadingAlerta(true);

        try {
            await apiMarketplaces.delete(`/mensagens/chat/${chatUuid}/alerta`);
            showSnackbar("Alerta removido com sucesso");
            setTimeout(() => {
                showSnackbar("O robô voltará a responder esse cliente", { severity: 'info' });
            }, 2000)
            return true;
        } catch (error) {
            showSnackbar("Ocorreu um erro ao remover o alerta", { severity: "error" });
        } finally {
            setLoadingAlerta(false);
        }
    }

    const deletarChat = async (chatUuid: string) => {
        if (loadingChats) {
            return false;
        }

        clearState(chatUuid);
        setLoadingChats(true);

        try {
            await apiMarketplaces.delete(`/mensagens/chat/${chatUuid}`);
            showSnackbar("Conversa excluída com sucesso");
            return true;
        } catch (error) {
            if (error instanceof AxiosError) {
                showSnackbar(error.response?.data.message, { severity: 'error' });
                return false;
            }
            showSnackbar("Ocorreu um erro ao excluir a conversa", { severity: "error" });
            return false;
        } finally {
            setLoadingChats(false);
        }
    }

    const enviarMensagem = async (chatUuid: string, mensagem: string, mensagemRespondida?: { identificadorExterno?: string; texto?: string; emissor?: string }) => {
        if (loadingEnvio) {
            return null;
        }

        setLoadingEnvio(true);

        try {
            const { data } = await apiMarketplaces.post(`/mensagens/chat/${chatUuid}/enviar`, {
                mensagem,
                ...(mensagemRespondida ? { mensagemRespondida } : {})
            });
            
            if (!data) {
                showSnackbar("Ocorreu um erro ao enviar a mensagem", { severity: "error" });
                return null;
            }
            
            return data;
        } catch (error) {
            if (error instanceof AxiosError) {
                showSnackbar(error.response?.data.message, { severity: 'error' });
                return null;
            }
            showSnackbar("Ocorreu um erro ao enviar a mensagem", { severity: "error" });
            return null;
        } finally {
            setLoadingEnvio(false);
        }
    }

    /**
     * Resumo de não lidas do estabelecimento — usado para inicializar o badge do
     * menu ao abrir o sistema e a cada reconexão do socket. Silencioso: falha
     * aqui não deve incomodar o usuário, o WebSocket corrige o número depois.
     */
    const buscarResumoNaoLidas = useCallback(async (): Promise<IResumoNaoLidas | null> => {
        try {
            const { data } = await apiMarketplaces.get('/mensagens/chat/nao-lidas');

            if (!data) {
                return null;
            }

            return {
                totalMensagens: Number(data.totalMensagens) || 0,
                totalConversas: Number(data.totalConversas) || 0,
            };
        } catch {
            return null;
        }
    }, [apiMarketplaces]);

    const marcarChatComoLido = useCallback(async (chatUuid: string) => {
        try {
            await apiMarketplaces.post(`/mensagens/chat/${chatUuid}/lido`);
            return true;
        } catch (error) {
            // Não exibir erro para o usuário, é uma operação silenciosa
            if (error instanceof AxiosError) {
                console.error('Erro ao marcar chat como lido:', error.response?.data.message);
            } else {
                console.error('Erro ao marcar chat como lido');
            }
            return false;
        }
    }, [apiMarketplaces]);

    const vincularCliente = useCallback(async (chatUuid: string, clienteUuid: string) => {
        try {
            await apiMarketplaces.post(`/mensagens/chat/${chatUuid}/vincular-cliente`, { clienteUuid });
            return true;
        } catch (error) {
            if (error instanceof AxiosError) {
                showSnackbar(error.response?.data.message || "Erro ao vincular cliente", { severity: 'error' });
            } else {
                showSnackbar("Erro ao vincular cliente", { severity: "error" });
            }
            return false;
        }
    }, [apiMarketplaces]);

    const desvincularCliente = useCallback(async (chatUuid: string) => {
        try {
            await apiMarketplaces.delete(`/mensagens/chat/${chatUuid}/vincular-cliente`);
            return true;
        } catch (error) {
            if (error instanceof AxiosError) {
                showSnackbar(error.response?.data.message || "Erro ao desvincular cliente", { severity: 'error' });
            } else {
                showSnackbar("Erro ao desvincular cliente", { severity: "error" });
            }
            return false;
        }
    }, [apiMarketplaces]);

    return { listarChats, buscarChatPorUuid, listarSessoes, removerAlerta, deletarChat, enviarMensagem, marcarChatComoLido, buscarResumoNaoLidas, vincularCliente, desvincularCliente, loadingChats, loadingSessoes, loadingAlerta, loadingEnvio }
}